export interface AuthUser {
  id: string
  name: string
  email: string | null
  avatarUrl: string | null
}

export interface AuthSession {
  user: AuthUser
  expiresAt: number | null
}

export type NativeOAuthHandlingResult =
  | { status: 'success'; session: AuthSession }
  | { status: 'ignored' }
  | { status: 'canceled' }

export interface SignInOptions {
  /** Make the person pick their Google account again, for actions that need a recent sign-in. */
  reauthenticate?: boolean
}

export interface AuthProvider {
  readonly id: string
  initialize(): Promise<void>
  signIn(options?: SignInOptions): Promise<AuthSession | null>
  signOut(): Promise<void>
  getSession(): Promise<AuthSession | null>
  onSessionChange(listener: (session: AuthSession | null) => void): () => void
  handleNativeOAuthCallback?(url: string): Promise<NativeOAuthHandlingResult>
  resumeNativeOAuthCallback?(): Promise<NativeOAuthHandlingResult>
  cancelPendingNativeOAuth?(): Promise<boolean>
}

export class LocalAuthProvider implements AuthProvider {
  readonly id = 'local'
  private session: AuthSession = {
    user: {
      id: 'local-user',
      name: 'Local workspace',
      email: null,
      avatarUrl: null,
    },
    expiresAt: null,
  }
  private readonly listeners = new Set<(session: AuthSession | null) => void>()

  async initialize(): Promise<void> {}

  async signIn(): Promise<AuthSession> {
    this.emit(this.session)
    return this.session
  }

  async signOut(): Promise<void> {
    this.emit(null)
  }

  async getSession(): Promise<AuthSession> {
    return this.session
  }

  onSessionChange(listener: (session: AuthSession | null) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emit(session: AuthSession | null): void {
    for (const listener of this.listeners) listener(session)
  }
}
